import { catchError, distinctUntilChanged, EMPTY, filter, map, merge, Observable, switchMap, take, tap } from 'rxjs';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { AccountManagerService } from '@app/api/account/account-manager.service';
import { BroadcastService } from '@app/common/services/broadcast.service';
import { DEFAULT_GUID } from '@app/shared/models';
import { ErrorHandlerService } from '@app/core/services';
import { IcAccount } from '@app/api/account/models/ic-account.model';
import { IcUserPreferences } from '@app/api/user-preference/models/ic-user-preferences.model';
import { ButtonComponent } from '@app/shared/button/button.component';
import { IconSvgComponent } from '@app/shared/icon-svg/icon-svg.component';
import { MenuIconsComponent } from '@app/shared/icon-svg/menu-icons/menu-icons.component';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { UserPreferenceManagerService } from '@app/api/user-preference/user-preference-manager.service';

/**
 * The header's account display for Toro users (roleId <= 6), who never get ic-toolbar-account-select: the header and
 * sidebar gate it behind `!user?.isToroUser()`, and its own getComponentData returns early for them. It shows the
 * preferred account's name behind a globe, beside an Admin button that opens the Admin Dashboard (PR 14043 review, AC 9
 * of AB#23533; ER-1572 for the globe and the ADMIN label). An Account Details button beside it opens that account's page,
 * /account/account-view/:id, which the retired dashboard bar's admin link (removed in AB#23400) used to open.
 *
 * Read-only by design. It never persists, never broadcasts and never switches: admins change their preferred account
 * from the Admin Dashboard tables, which persist and broadcast through UserPreferenceManagerService, and this only
 * follows that broadcast. It also never asks for the account list - GET /api/accounts answers admins with
 * 400 / 10011 and the interceptor redirects to /home/admin - so the name is read one account at a time.
 */
@Component({
    selector: 'ic-toolbar-admin-account',
    templateUrl: './toolbar-admin-account.component.html',
    styleUrls: ['./toolbar-admin-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { '[class.ic-admin-tb-host--header]': 'placement() === \'header\'' },
    imports: [ButtonComponent, IconSvgComponent, MenuIconsComponent, TranslatePipe]
})
export class ToolbarAdminAccountComponent {
    private accountManager = inject(AccountManagerService);
    private broadcastService = inject(BroadcastService);
    private errorHandler = inject(ErrorHandlerService);
    private router = inject(Router);
    private userPreferenceManager = inject(UserPreferenceManagerService);

    /** Where the display sits. The header fills its account segment and compacts the button; the sidebar keeps 300px. */
    readonly placement = input<'header' | 'sidebar'>('sidebar');

    /** The Admin Dashboard button was pressed. Lets the sidebar close itself. */
    readonly adminDashboardClicked = output<void>();

    /** The Account Details button was pressed. Lets the sidebar close itself. */
    readonly accountDetailsClicked = output<void>();

    // Declared before the streams: toSignal subscribes as the account field initialises, and the stored preference can
    // answer synchronously, so a later initialiser would reset the id it has just written.
    private accountId: string | null = null;

    // The stored preference first, then every switch after it. selectedAccountChange is a ReplaySubject(1), so on boot
    // it usually replays the same id the preference holds; distinctUntilChanged loads it once.
    private readonly selection$: Observable<IcAccount> = merge(this.getPreferredAccountId(), this.broadcastService.selectedAccountChange)
        .pipe(
            filter((accountId: string) => accountId != null && accountId !== DEFAULT_GUID),
            distinctUntilChanged(),
            switchMap((accountId: string) => this.loadAccount(accountId)),
        );

    // A rename of the account on screen. The cache is refreshed before this fires, so the re-read is current.
    private readonly rename$: Observable<IcAccount> = this.broadcastService.accountUpdated
        .pipe(
            filter((accountId: string) => accountId === this.accountId),
            switchMap((accountId: string) => this.loadAccount(accountId)),
            // A switch may have landed while the re-read was in flight; never put the old account back.
            filter((account: IcAccount) => account?.accountID === this.accountId),
        );

    // A signal, so the name redraws under OnPush (CODING-STANDARDS 5). toSignal subscribes here, in the injection
    // context, and unsubscribes when the component is destroyed.
    protected readonly account = toSignal(
        merge(this.selection$, this.rename$)
            .pipe(tap((account: IcAccount) => { this.accountId = account?.accountID ?? null; })),
        { initialValue: null },
    );

    /** Null until an account resolves. With no preferred account the template renders the ADMIN button alone. */
    protected readonly accountName = computed(() => this.account()?.accountName ?? null);

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    /** Opens the account's page. The account is already the preferred one, so opening it needs no switch. */
    protected onAccountDetailsClick(): void {
        const accountId = this.account()?.accountID;
        if (!accountId) {
            return;
        }

        this.accountDetailsClicked.emit();
        this.router.navigate(['/account/account-view', accountId]);
    }

    protected onAdminDashboardClick(): void {
        this.adminDashboardClicked.emit();
        this.router.navigateByUrl('/home/admin');
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    /** A failed preference read is reported and ends only this branch, so later switches still show. */
    private getPreferredAccountId(): Observable<string> {
        return this.userPreferenceManager.getUserPreferences()
            .pipe(
                take(1),
                map((preferences: IcUserPreferences) => preferences?.defaultAccount),
                catchError(err => {
                    this.errorHandler.handleError(err);
                    return EMPTY;
                }),
            );
    }

    /** Caught per lookup, so one failed read is reported without ending the stream it sits in. */
    private loadAccount(accountId: string): Observable<IcAccount> {
        return this.accountManager.getAccountById(accountId)
            .pipe(
                catchError(err => {
                    this.errorHandler.handleError(err);
                    return EMPTY;
                }),
            );
    }
}
